import {
  useContestPendingSetupSave,
  useContestUpdate,
} from "@sportsmet/contest-sdk/private";
import {
  useBannerSet,
  usePoolCreate,
  usePoolCreateOptions,
  useQuickStartList,
} from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  OVERLAY_FOOTER_LAYOUT,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { CreatePoolBasicsStep } from "./CreatePoolBasicsStep";
import {
  CreatePoolCreated,
  type typeCreatePoolSetupPending,
} from "./CreatePoolCreated";
import {
  applyCreatePoolDefaults,
  buildCreatePoolBody,
  buildPoolTypeRows,
  chooseContestType,
  chooseLeague,
  chooseQuickStart,
  clearQuickStart,
  EMPTY_CREATE_POOL_DRAFT,
  isCreatePoolDraftDirty,
  isPoolNameValid,
  missingCreatePoolBasicsItems,
  POOL_NAME_REQUIRED_ERROR,
  poolEligibleLeagueOptions,
  withCustomLeagueOption,
  type typeCreatePoolDraft,
  type typeQuickStartOption,
} from "./createPoolDraft";
import { CreatePoolLeagueStep } from "./CreatePoolLeagueStep";
import {
  CREATE_POOL_MISSING_ELEMENT_ID,
  CreatePoolMissingSummary,
} from "./CreatePoolMissingSummary";
import { CreatePoolReviewStep } from "./CreatePoolReviewStep";
import { CreatePoolRulesStep } from "./CreatePoolRulesStep";
import {
  CreatePoolSetupStep,
  type typeCreatePoolSetupChoice,
} from "./CreatePoolSetupStep";
import {
  CREATE_POOL_FIRST_STEP,
  createPoolStepCounterLabel,
  createPoolStepTitle,
  isLastCreatePoolStep,
  nextCreatePoolStep,
  previousCreatePoolStep,
  type typeCreatePoolStepId,
  visibleCreatePoolSteps,
} from "./createPoolSteps";
import { CreatePoolTypeSetupStep } from "./CreatePoolTypeSetupStep";
import {
  createPoolSubmitLabel,
  isSetupSlotPoolType,
  poolTypeSetupMissingItems,
} from "./poolTypeSetups";
import {
  buildContestConfig,
  isRulesComplete,
  missingSurvivorRulesItems,
} from "./survivorRulesDraft";

export const CREATE_POOL_DISCARD_TITLE = "Discard your changes?";
export const CREATE_POOL_DISCARD_BODY =
  "You'll lose everything you've set up so far.";
export const CREATE_POOL_DISCARD_CONFIRM_LABEL = "Discard";
export const CREATE_POOL_DISCARD_CANCEL_LABEL = "Keep editing";
export const CREATE_POOL_CREATED_TITLE = "Your pool is ready";
export const CONTEST_NAME_MAX_LENGTH = 60;

export type typeCreatePoolDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (poolId: string) => void;
  /** Opens the new pool with its invite sheet up (ISSUE-1345). */
  onInvitePeople: (poolId: string) => void;
};

type typeCreatedPool = {
  poolId: string;
  joinCode: string;
  joinUrl: string;
  contestId: string | null;
  // ISSUE-1316. Null once the contest exists. Otherwise says whether the
  // rules the fan picked were saved for later.
  setupPending: typeCreatePoolSetupPending | null;
};

/**
 * The one stateful component of the create-pool flow: fetches the catalog
 * and the create options, holds the draft and the current step, runs the
 * discard guard, submits, and switches to the created screen.
 */
export const CreatePoolDialog = (
  props: typeCreatePoolDialogProps,
): ReactNode => {
  const [draft, setDraft] = useState<typeCreatePoolDraft>(
    EMPTY_CREATE_POOL_DRAFT,
  );
  const [stepId, setStepId] = useState<typeCreatePoolStepId>(
    CREATE_POOL_FIRST_STEP,
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [created, setCreated] = useState<typeCreatedPool | null>(null);
  const [setupChoice, setSetupChoice] =
    useState<typeCreatePoolSetupChoice | null>(null);
  // ISSUE-1093. True from a Continue on step 1 pressed while the Quick start
  // read is still in flight until that read answers, so the dialog knows
  // whether the setup step has anything to offer before it moves on.
  const [isAwaitingQuickStarts, setIsAwaitingQuickStarts] = useState(false);
  const baselineRef = useRef<typeCreatePoolDraft | null>(null);
  const hasAppliedDefaults = useRef(false);
  const startedPoolRef = useRef<typeCreatedPool | null>(null);

  const catalog = useLeagueCatalogGet({});
  const options = usePoolCreateOptions({});
  const create = usePoolCreate();
  const contestUpdate = useContestUpdate();
  const pendingSetupSave = useContestPendingSetupSave();
  const bannerSet = useBannerSet();
  const quickStarts = useQuickStartList(
    { leagueKey: draft.leagueKey ?? "" },
    { enabled: draft.leagueKey !== null },
  );

  const leagueOptions = useMemo(
    () =>
      withCustomLeagueOption(
        poolEligibleLeagueOptions(catalog.data?.data?.sports ?? null),
        options.data?.data?.contestTypes ?? [],
      ),
    [catalog.data, options.data],
  );
  const poolTypeRows = useMemo(
    () =>
      buildPoolTypeRows(
        options.data?.data?.contestTypes ?? [],
        draft.leagueKey,
      ),
    [options.data, draft.leagueKey],
  );
  const contestTypeLabel = useMemo(
    () =>
      poolTypeRows.find((row) => row.contestType === draft.contestType)
        ?.label ?? "",
    [poolTypeRows, draft.contestType],
  );

  const isCatalogLoading = catalog.isLoading || options.isLoading;
  const hasCatalogFailed =
    catalog.isError ||
    options.isError ||
    Boolean(catalog.data?.error) ||
    Boolean(options.data?.error);

  const quickStartOptions: readonly typeQuickStartOption[] = useMemo(
    () =>
      (quickStarts.data?.data?.items ?? []).map((item) => ({
        quickStartId: item.quickStartId,
        title: item.title,
        summary: item.summary,
        config: item.config,
      })),
    [quickStarts.data],
  );
  const isQuickStartsLoading =
    draft.leagueKey !== null && quickStarts.isLoading;
  const hasQuickStartsFailed =
    quickStarts.isError || Boolean(quickStarts.data?.error);
  // /quick-start/list is keyed by league only, so the chosen contest type is
  // matched here (ISSUE-1093). Every Quick start is Survivor today, which
  // makes this a no-op until another contest type ships one.
  const matchingQuickStartOptions = useMemo(
    () =>
      quickStartOptions.filter(
        (option) => option.config.contestType === draft.contestType,
      ),
    [quickStartOptions, draft.contestType],
  );
  const hasQuickStarts =
    !hasQuickStartsFailed && matchingQuickStartOptions.length > 0;
  // ISSUE-1093. With no Quick start the setup step would be one preselected
  // option, so it is skipped. Only on a successful read: a failed read keeps
  // the step, which carries the retry. Never while the fan is on it.
  const isSetupSkipped =
    stepId !== "setup" &&
    draft.leagueKey !== null &&
    quickStarts.isSuccess &&
    !hasQuickStartsFailed &&
    matchingQuickStartOptions.length === 0;
  const steps = visibleCreatePoolSteps({ isSetupSkipped });
  // ISSUE-1303. Past the first step the total is settled: the setup step is
  // either on the list or skipped for good.
  const isStepTotalKnown =
    stepId !== "league" ||
    (draft.leagueKey !== null &&
      draft.contestType !== null &&
      !isQuickStartsLoading &&
      (quickStarts.isSuccess || hasQuickStartsFailed));

  function advanceFromLeague(): void {
    if (isSetupSkipped) setSetupChoice("myself");
    setStepId(nextCreatePoolStep("league", steps) ?? "setup");
  }

  useEffect(() => {
    if (!isAwaitingQuickStarts || isQuickStartsLoading) return;
    setIsAwaitingQuickStarts(false);
    advanceFromLeague();
    // advanceFromLeague reads isSetupSkipped and steps from this same render,
    // and isSetupSkipped is in the list, so it is never stale here.
  }, [isAwaitingQuickStarts, isQuickStartsLoading, isSetupSkipped]);

  useEffect(() => {
    const optionsData = options.data?.data;
    if (optionsData === undefined || optionsData === null) return;
    if (hasAppliedDefaults.current) return;
    hasAppliedDefaults.current = true;
    setDraft((current) => {
      const next = applyCreatePoolDefaults(current, optionsData);
      baselineRef.current = next;
      return next;
    });
  }, [options.data]);

  const isDirty =
    created === null &&
    baselineRef.current !== null &&
    isCreatePoolDraftDirty(draft, baselineRef.current);

  function resetAndClose(): void {
    setDraft(EMPTY_CREATE_POOL_DRAFT);
    setStepId(CREATE_POOL_FIRST_STEP);
    setNameError(null);
    setCreated(null);
    setSetupChoice(null);
    setIsAwaitingQuickStarts(false);
    hasAppliedDefaults.current = false;
    baselineRef.current = null;
    startedPoolRef.current = null;
    create.reset();
    contestUpdate.reset();
    pendingSetupSave.reset();
    bannerSet.reset();
    props.onClose();
  }

  async function handleRequestClose(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: CREATE_POOL_DISCARD_TITLE,
      body: CREATE_POOL_DISCARD_BODY,
      confirmLabel: CREATE_POOL_DISCARD_CONFIRM_LABEL,
      cancelLabel: CREATE_POOL_DISCARD_CANCEL_LABEL,
    });
    if (isConfirmed) resetAndClose();
  }

  async function handleSubmit(): Promise<void> {
    const body = buildCreatePoolBody(draft);
    if (body === null) return;
    // ISSUE-1258. Only Survivor configures its contest after create. Every
    // other type's setup travels inside /pool/create.
    const isSurvivor = body.contestType === "survivor";
    const config = isSurvivor ? buildContestConfig(draft.rules) : null;
    if (isSurvivor && config === null) return;

    try {
      if (startedPoolRef.current === null) {
        const response = await create.mutateAsync(body);
        // ISSUE-1302. A Pick'em pool on a league with no published schedule
        // answers CONTEST_TYPE_NOT_READY; the poolCreate context says why.
        if (showApiError(response, "poolCreate")) return;
        const data = response.data;
        if (!data) return;
        startedPoolRef.current = {
          poolId: data.poolId,
          joinCode: data.joinCode,
          joinUrl: data.joinUrl,
          contestId: data.contestId ?? null,
          setupPending: null,
        };

        // A failed attach never fails the create: the pool exists, and
        // refusing the whole flow over a banner would be worse for the fan
        // than a pool with no banner yet.
        if (draft.bannerMediaId !== null) {
          try {
            await bannerSet.mutateAsync({
              poolId: data.poolId,
              bannerMediaId: draft.bannerMediaId,
            });
          } catch {
            // continue regardless
          }
        }
      }

      const started = startedPoolRef.current;
      if (config === null) {
        setCreated(started);
        return;
      }
      if (started.contestId === null) {
        // The league's season data is not published yet, so /pool/create's
        // server-side bootstrap skipped the contest (SUR-R6). The pool
        // exists either way, so the fan still reaches the created screen;
        // the old flow failed the whole submit here with
        // PROVIDER_SEASON_DATA_MISSING in exactly this case.
        //
        // ISSUE-1316. The rules the fan just picked are saved for the
        // contest, so New contest starts from them once the schedule is out.
        // The created screen says which way it went; a failed save never
        // fails the create, the pool already exists.
        let isRulesSaved = false;
        try {
          const saveResponse = await pendingSetupSave.mutateAsync({
            poolId: started.poolId,
            name: body.name.trim().slice(0, CONTEST_NAME_MAX_LENGTH),
            config,
          });
          isRulesSaved = !saveResponse.error && Boolean(saveResponse.data);
        } catch {
          isRulesSaved = false;
        }
        setCreated({
          ...started,
          setupPending: isRulesSaved ? "rulesSaved" : "rulesNotSaved",
        });
        return;
      }

      const contestResponse = await contestUpdate.mutateAsync({
        poolId: started.poolId,
        contestId: started.contestId,
        ...config,
        // The first configure of a new pool is setup, not a change, so
        // contest-api does not announce it in the pool chat (ISSUE-1041).
        isInitialSetup: true,
      });
      if (showApiError(contestResponse)) return;
      if (!contestResponse.data) return;

      setCreated(started);
    } catch (error) {
      showApiError(error);
    }
  }

  const setupSlotType = isSetupSlotPoolType(draft.contestType)
    ? draft.contestType
    : null;
  const missingItems: readonly string[] =
    stepId === "basics"
      ? missingCreatePoolBasicsItems(draft)
      : stepId === "rules"
        ? setupSlotType !== null
          ? poolTypeSetupMissingItems(setupSlotType, draft.typeSetups)
          : missingSurvivorRulesItems(draft.rules)
        : [];

  const canAdvance =
    stepId === "league"
      ? draft.leagueKey !== null &&
        draft.contestType !== null &&
        // A type the league cannot run (Squares in the WNBA, Survivor with
        // no league) stays picked but greyed out, and blocks Next.
        poolTypeRows.some(
          (row) => row.contestType === draft.contestType && row.isAvailable,
        )
      : stepId === "setup"
        ? // Never advance while the list is still in flight: `hasQuickStarts`
          // is false during a pending query, which would otherwise let a fan
          // on a slow connection click straight past the templates.
          !isQuickStartsLoading &&
          (!hasQuickStarts ||
            setupChoice === "myself" ||
            draft.quickStartId !== null)
        : stepId === "basics"
          ? missingItems.length === 0
          : stepId === "rules"
            ? missingItems.length === 0
            : buildCreatePoolBody(draft) !== null &&
              (setupSlotType !== null || isRulesComplete(draft.rules));

  const isSubmitting =
    create.isPending || contestUpdate.isPending || pendingSetupSave.isPending;

  const title =
    created !== null
      ? CREATE_POOL_CREATED_TITLE
      : createPoolStepTitle(stepId, draft.contestType);
  const description =
    created !== null
      ? undefined
      : createPoolStepCounterLabel(stepId, steps, isStepTotalKnown);

  const body =
    created !== null ? (
      <CreatePoolCreated
        joinCode={created.joinCode}
        joinUrl={created.joinUrl}
        setupPending={created.setupPending}
        poolName={draft.name}
      />
    ) : stepId === "league" ? (
      <CreatePoolLeagueStep
        draft={draft}
        leagueOptions={leagueOptions}
        poolTypeRows={poolTypeRows}
        isLoading={isCatalogLoading}
        hasFailed={hasCatalogFailed}
        onRetry={() => {
          void catalog.refetch();
          void options.refetch();
        }}
        onChooseLeague={(option) => {
          setDraft((current) => chooseLeague(current, option));
          // chooseLeague drops the template, so the setup step becomes an
          // unanswered question again for the newly chosen league.
          setSetupChoice(null);
        }}
        onChooseContestType={(contestType) => {
          setDraft((current) => chooseContestType(current, contestType));
        }}
      />
    ) : stepId === "setup" ? (
      <CreatePoolSetupStep
        choice={setupChoice}
        quickStarts={matchingQuickStartOptions}
        selectedQuickStartId={draft.quickStartId}
        isLoading={isQuickStartsLoading}
        hasFailed={hasQuickStartsFailed}
        onRetry={() => {
          void quickStarts.refetch();
        }}
        onChooseChoice={(choice) => {
          setSetupChoice(choice);
          if (choice === "myself") {
            setDraft((current) => clearQuickStart(current));
          }
        }}
        onChooseQuickStart={(option) => {
          setDraft((current) => chooseQuickStart(current, option));
          setStepId("basics");
        }}
      />
    ) : stepId === "basics" ? (
      <CreatePoolBasicsStep
        draft={draft}
        nameError={nameError}
        onChangeName={(name) => {
          setDraft((current) => ({ ...current, name }));
          setNameError(null);
        }}
        onBlurName={() => {
          setNameError(
            isPoolNameValid(draft) ? null : POOL_NAME_REQUIRED_ERROR,
          );
        }}
        onChangeDraft={(next) => {
          setDraft(next);
        }}
      />
    ) : stepId === "rules" && setupSlotType !== null ? (
      <CreatePoolTypeSetupStep
        contestType={setupSlotType}
        draft={draft}
        onChangeSetups={(typeSetups) => {
          setDraft((current) => ({ ...current, typeSetups }));
        }}
      />
    ) : stepId === "rules" ? (
      <CreatePoolRulesStep
        rules={draft.rules}
        onChangeRules={(rules) => {
          setDraft((current) => ({ ...current, rules }));
        }}
      />
    ) : (
      <CreatePoolReviewStep
        draft={draft}
        leagueOptions={leagueOptions}
        contestTypeLabel={contestTypeLabel}
      />
    );

  const footer =
    created !== null ? (
      <div className={OVERLAY_FOOTER_LAYOUT}>
        <Button
          variant="secondary"
          onClick={() => {
            const poolId = created.poolId;
            resetAndClose();
            props.onInvitePeople(poolId);
          }}
          data-testid="create-pool-invite-people"
        >
          Invite people
        </Button>
        <Button
          onClick={() => {
            const poolId = created.poolId;
            resetAndClose();
            props.onCreated(poolId);
          }}
          data-testid="create-pool-go"
        >
          Go to your pool
        </Button>
      </div>
    ) : (
      <div className="flex w-full flex-col gap-2">
        <CreatePoolMissingSummary items={missingItems} />
        <div className={OVERLAY_FOOTER_LAYOUT}>
          {previousCreatePoolStep(stepId, steps) !== null ? (
            <Button
              variant="secondary"
              onClick={() => {
                const back = previousCreatePoolStep(stepId, steps);
                if (back !== null) {
                  setStepId(back);
                  setNameError(null);
                }
              }}
              data-testid="create-pool-back"
            >
              Back
            </Button>
          ) : null}
          {isLastCreatePoolStep(stepId, steps) ? (
            <Button
              loading={isSubmitting}
              loadingLabel="Creating your pool"
              disabled={!canAdvance || isSubmitting}
              onClick={() => {
                void handleSubmit();
              }}
              data-testid="create-pool-submit"
            >
              {createPoolSubmitLabel(draft.contestType, draft.typeSetups)}
            </Button>
          ) : (
            <Button
              loading={stepId === "league" && isAwaitingQuickStarts}
              loadingLabel="Checking for ready-made setups"
              disabled={!canAdvance || isAwaitingQuickStarts}
              aria-describedby={
                missingItems.length > 0
                  ? CREATE_POOL_MISSING_ELEMENT_ID
                  : undefined
              }
              onClick={() => {
                if (stepId === "league") {
                  if (isQuickStartsLoading) {
                    setIsAwaitingQuickStarts(true);
                    return;
                  }
                  advanceFromLeague();
                  return;
                }
                const next = nextCreatePoolStep(stepId, steps);
                if (next !== null) setStepId(next);
              }}
              data-testid="create-pool-continue"
            >
              Continue
            </Button>
          )}
        </div>
      </div>
    );

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) resetAndClose();
      }}
      onRequestClose={() => {
        void handleRequestClose();
      }}
      isDirty={isDirty}
      title={title}
      description={description}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
